import { Link, useNavigate } from 'react-router-dom'
import { BookOpen, Bookmark } from 'lucide-react'
import { cn } from '../../app/libs/cn'
import { type ApiBookListItem, bookDiscountPct } from '@/services/bookService'

// ภาพปกหนังสือ — ใช้ภาพแทน (สีไล่ + ไอคอน) จนกว่าจะมี cover_url จริง
const COVER_GRADIENTS = [
  'from-indigo-500 via-violet-500 to-purple-600',
  'from-cyan-400 via-sky-500 to-blue-600',
  'from-emerald-400 via-teal-500 to-green-600',
  'from-amber-400 via-orange-500 to-yellow-600',
]

export function BookCover({ book, className }: { book: Pick<ApiBookListItem, 'book_id' | 'title' | 'cover_url' | 'category_id'>; className?: string }) {
  const gradient = COVER_GRADIENTS[book.category_id % COVER_GRADIENTS.length]
  return (
    <div className={cn('relative aspect-[3/4] w-full overflow-hidden bg-gradient-to-br', gradient, className)}>
      {book.cover_url ? (
        <img src={book.cover_url} alt={book.title} className="h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center text-white/90">
          <BookOpen className="h-12 w-12 opacity-80" strokeWidth={1.4} />
          <p className="line-clamp-4 text-sm font-semibold leading-snug">{book.title}</p>
        </div>
      )}
    </div>
  )
}

export default function BookCard({ book }: { book: ApiBookListItem }) {
  const discount = bookDiscountPct(book)
  const navigate = useNavigate()
  const instructorAvatar = book.instructor_avatar_url
    || (book.instructor_name ? `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(book.instructor_name)}` : '')
  // การ์ดทั้งใบเป็น Link ไปหน้าหนังสือ — แถวผู้สอนกดแยกไปหน้าผู้สอน (แบบเดียวกับ CourseCard)
  const openInstructor = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (book.instructor_id) navigate(`/instructors/${book.instructor_id}`)
  }
  return (
    <Link
      to={`/books/${book.slug}`}
      className="group flex flex-col overflow-hidden rounded-sm border border-border bg-surface transition-shadow hover:shadow-[0_8px_24px_rgba(108,99,255,0.14)]"
    >
      <div className="relative">
        <BookCover book={book} />
        {/* ปุ่มบุ๊กมาร์ก — หน้าตาเท่านั้น ยังไม่มี backend เก็บรายการที่บันทึก */}
        <button
          type="button"
          aria-label="บันทึกหนังสือ"
          onClick={(e) => e.preventDefault()}
          className="absolute right-3 top-3 rounded-full bg-white/90 p-1.5 text-text-muted shadow-sm transition hover:text-accent"
        >
          <Bookmark className="h-4 w-4" />
        </button>
        <div className="absolute left-3 top-3 flex gap-1.5">
          {!book.in_stock && <span className="rounded-sm bg-gray-700 px-2 py-0.5 text-xs font-bold text-white">สินค้าหมด</span>}
          {book.is_bestseller && <span className="rounded-sm bg-amber-400 px-2 py-0.5 text-xs font-bold text-black">ขายดี</span>}
          {discount && <span className="rounded-sm bg-rose-500 px-2 py-0.5 text-xs font-bold text-white">-{discount}%</span>}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-4">
        <span className="mb-2 w-fit rounded-full bg-accent/10 px-2.5 py-0.5 text-[11px] font-medium text-accent">{book.category_name}</span>
        <h3 className="text-sm font-semibold leading-snug text-text group-hover:text-accent">{book.title}</h3>
        {/* ชื่อหนังสือยาวไม่เท่ากัน — ดันแถวผู้สอน+ราคาลงล่างสุดของการ์ด (mt-auto) ให้ทุกใบอยู่ระดับเดียวกัน */}
        <div className="mt-auto pt-2">
          {book.instructor_id && book.instructor_name ? (
            <button type="button" onClick={openInstructor} className="flex h-8 items-center gap-2 text-left">
              <span className="h-6 w-6 shrink-0 overflow-hidden rounded-full bg-surface-2">
                <img src={instructorAvatar} alt={book.instructor_name} className="h-full w-full object-cover" />
              </span>
              <span className="truncate text-xs font-medium text-text hover:text-accent">{book.instructor_name}</span>
            </button>
          ) : (
            <p className="flex h-8 items-center text-xs text-text-muted">{book.author_name}</p>
          )}
        </div>
        <div className="flex items-baseline gap-2 pt-1">
          <span className="text-lg font-bold text-text">฿{book.price.toLocaleString()}</span>
          {book.original_price && <span className="text-xs text-text-muted line-through">฿{book.original_price.toLocaleString()}</span>}
          {book.page_count ? <span className="ml-auto text-xs text-text-muted">{book.page_count.toLocaleString('th-TH')} หน้า</span> : null}
        </div>
      </div>
    </Link>
  )
}
